import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import Footer from "../components/Footer";
import RegistrationForm from "../components/RegistrationForm";
import SiteHeader from "../components/SiteHeader";
import TrustBar from "../components/TrustBar";
import UpcomingLabs from "../components/UpcomingLabs";
import { TRUST_LOGOS, getAssociationBySlug } from "../data/associations";
import { getUpcomingLabs } from "../firebase";
import {
  formatLabDateInUserTimeZone,
  formatLabDateShortInUserTimeZone,
  formatLabTimeRangeInUserTimeZone,
  getUserTimeZoneName,
} from "../utils/datetime";

const registerButtonText = "Register free in 15 seconds";
const associationFormButtonText = "Get my free AI Lab invite";
const associationRegisterHeading = "Step 1 of 1: Tell us where to send your AI Lab invites";

const MOBILE_BREAKPOINT_QUERY = "(max-width: 800px)";

const isMobileViewport = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
};

function AssociationLandingPage() {
  const { slug } = useParams();
  const association = getAssociationBySlug(slug);

  const [upcomingLabs, setUpcomingLabs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isActive = true;

    const loadLabs = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await getUpcomingLabs(3);
        if (isActive) {
          setUpcomingLabs(response);
        }
      } catch (loadError) {
        console.error("Failed to load upcoming labs for association page:", loadError);
        if (isActive) {
          setError(
            "We couldn't load upcoming lab sessions right now. Please refresh in a moment."
          );
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadLabs();

    return () => {
      isActive = false;
    };
  }, []);

  if (!association) {
    return <Navigate to="/" replace />;
  }

  const nextLab = upcomingLabs[0] || null;
  const associationBadgeName = association.abbreviation || association.name;
  const memberBenefitLine = `Free member benefit from ${associationBadgeName}`;
  const userTimeZoneName = getUserTimeZoneName();
  const nextLabLocalDate = nextLab ? formatLabDateInUserTimeZone(nextLab.dateTime) : "";
  const nextLabShortDate = nextLab
    ? formatLabDateShortInUserTimeZone(nextLab.dateTime)
    : "";
  const nextLabLocalTime = nextLab
    ? formatLabTimeRangeInUserTimeZone(nextLab.dateTime, nextLab.durationMinutes)
    : "";
  const nextLabDescription =
    (nextLab?.shortDescription || nextLab?.longDescription || "").trim();
  const preSubmitNote = nextLab
    ? `Next live lab: ${nextLabShortDate} @ ${nextLabLocalTime}. If you can't attend live, we'll send the recording.`
    : "You'll get an invite for the next published Lab. If you can't attend live, we'll send the recording.";

  const scrollToRegister = () => {
    const desktopTarget = document.getElementById("association-register-desktop");
    const mobileTarget = document.getElementById("association-register-mobile");

    const target = isMobileViewport()
      ? mobileTarget || desktopTarget
      : desktopTarget || mobileTarget;

    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderStepStrip = () => (
    <div className="association-steps-strip" aria-label="Registration steps">
      <article className="association-step-item">
        <span className="association-step-glyph">1</span>
        <p>You fill out this form once</p>
      </article>
      <article className="association-step-item">
        <span className="association-step-glyph">2</span>
        <p>We email your Zoom link + calendar invite</p>
      </article>
      <article className="association-step-item">
        <span className="association-step-glyph">3</span>
        <p>You join live or watch the recording</p>
      </article>
    </div>
  );

  return (
    <>
      <SiteHeader
        registerLabel={registerButtonText}
        onRegisterClick={scrollToRegister}
      />

      <main>
        <section className="hero section association-hero">
          <div className="container association-hero-grid">
            <div className="association-hero-main">
              <p className="association-message-match">
                You clicked from {association.name}'s email - you're in the right place.
              </p>
              <h1>Save your spot for the Monthly AI Lab for School Leaders</h1>
              <p className="hero-copy">
                Fill out this 15-second form once and we'll send you Zoom links,
                calendar holds, and recordings for every monthly Lab, free for{" "}
                {association.name} members.
              </p>

              <article className="card association-next-lab-card">
                <p className="association-next-lab-label">
                  Next live Lab:
                </p>
                {nextLab ? (
                  <>
                    <p className="association-next-lab-date">{nextLabLocalDate}</p>
                    <p className="association-next-lab-time">{nextLabLocalTime}</p>
                    <h3 className="association-next-lab-title">{nextLab.title}</h3>
                    {nextLabDescription ? (
                      <p className="association-next-lab-description">{nextLabDescription}</p>
                    ) : null}
                  </>
                ) : (
                  <p className="association-next-lab-empty">
                    Next live Lab date will be posted soon.
                  </p>
                )}
              </article>

              <div className="association-brand-row">
                <img
                  className="association-logo"
                  src={association.logo}
                  alt={`${association.name} logo`}
                />
              </div>

              <button
                className="btn btn-primary association-mobile-register-btn"
                type="button"
                onClick={scrollToRegister}
              >
                {registerButtonText}
              </button>
            </div>

            <aside id="association-register-desktop" className="card association-register-desktop">
              <p className="association-member-badge">{memberBenefitLine}</p>
              <h2 className="association-register-title">{associationRegisterHeading}</h2>
              <p className="association-register-microcopy">
                One form covers all future Labs. Takes about 15 seconds.
              </p>
              <RegistrationForm
                nextLab={nextLab}
                submitLabel={associationFormButtonText}
                preSubmitNote={preSubmitNote}
                variant="association"
                autoScrollToConfirmation
                source={{
                  sourceType: "association",
                  sourceSlug: association.slug,
                  sourceName: association.name,
                }}
              />
              {renderStepStrip()}
            </aside>
          </div>
        </section>

        <section id="association-register-mobile" className="section association-mobile-register-section">
          <div className="container narrow">
            <p className="association-member-badge">{memberBenefitLine}</p>
            <h2>{associationRegisterHeading}</h2>
            <p className="association-register-microcopy">
              One form covers all future Labs. Takes about 30 seconds.
            </p>

            <div className="card association-mobile-register-card">
              <RegistrationForm
                nextLab={nextLab}
                submitLabel={associationFormButtonText}
                preSubmitNote={preSubmitNote}
                variant="association"
                source={{
                  sourceType: "association",
                  sourceSlug: association.slug,
                  sourceName: association.name,
                }}
              />
              {renderStepStrip()}
            </div>
          </div>
        </section>

        <UpcomingLabs
          labs={upcomingLabs}
          isLoading={isLoading}
          error={error}
          onRegisterClick={scrollToRegister}
          registerButtonLabel={registerButtonText}
        />

        <TrustBar logos={TRUST_LOGOS} />
      </main>

      <Footer />
    </>
  );
}

export default AssociationLandingPage;
